import { usePublishLanguage } from '@src/hooks/usePublishLanguage';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components';

import InfoIcon from '../../svgs/InfoIcon';
import { DialogTitle } from '../common/dialogStyles';
import Modal from '../common/Modal';
import { PublishVoiceNotice } from './PublishVoiceNotice';

type Props = {
  closeModal: () => void;
};

const RetryPublishModal: React.FC<Props> = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const { renderUsingChunks } = useFlagsCombination(userIdentity.currentRole);
  const publishLanguage = usePublishLanguage();
  const { closeModal } = props;

  const handleRetryPublishing = async () => {
    await videoCreator.publishVideo(
      videoCreator.renderVideoResLevel!,
      renderUsingChunks,
      publishLanguage,
    );
  };

  return (
    <Modal isOpen closeModal={closeModal}>
      <Wrapper>
        <Heading>
          <InfoIcon /> <span>Publishing failed</span>
        </Heading>
        {/* PLAT-10629 step 11 — which voice this retry will deliver. */}
        <PublishVoiceNotice />
        <Buttons>
          <CancelButton onClick={closeModal}>Cancel</CancelButton>
          <SaveButton onClick={handleRetryPublishing}>Retry</SaveButton>
        </Buttons>
      </Wrapper>
    </Modal>
  );
});

export default RetryPublishModal;

const Wrapper = styled.div`
  width: 459px;
  height: 200px;
  border-radius: 16px;
  border: 1px solid var(--border);
  padding: 48px 24px;
  box-sizing: border-box;
  background: var(--background);
`;

const Heading = styled(DialogTitle)`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Buttons = styled.div`
  margin-top: 32px;
  display: flex;
  height: 48px;
  gap: 8px;
`;

const CancelButton = styled.div`
  box-sizing: border-box;
  flex: 1;
  display: flex;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid var(--destructive);
  cursor: pointer;

  font-size: var(--text-sm);
  font-weight: var(--font-weight-bold);

  color: var(--destructive);
`;

const SaveButton = styled(CancelButton)`
  background: var(--destructive);
  color: var(--background);
`;
